import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { CompactHintTrigger } from "./compact-hint-trigger";

interface CompactCardProps {
  title: React.ReactNode;
  badge?: React.ReactNode;
  actions?: React.ReactNode;
  /** Ayuda en tooltip junto al título. No se pinta como párrafo en el cuerpo. */
  hint?: string;
  className?: string;
  bodyClassName?: string;
  children: React.ReactNode;
  collapsible?: boolean;
  defaultOpen?: boolean;
  /** Modo controlado: estado abierto/cerrado del collapsible. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  summary?: React.ReactNode;
}

function CardHint({ title, hint }: { title: React.ReactNode; hint?: string }) {
  if (!hint) return null;
  const hintLabel = typeof title === "string" ? title : "Ayuda";
  return <CompactHintTrigger text={hint} label={hintLabel} />;
}

export const CompactCard: React.FC<CompactCardProps> = ({
  title,
  badge,
  actions,
  hint,
  className,
  bodyClassName,
  children,
  collapsible = true,
  defaultOpen = true,
  open: openProp,
  onOpenChange,
  summary,
}) => {
  const [internalOpen, setInternalOpen] = useState(defaultOpen);
  const isControlled = openProp !== undefined;
  const open = isControlled ? openProp : internalOpen;
  const setOpen = (next: boolean) => {
    if (!isControlled) setInternalOpen(next);
    onOpenChange?.(next);
  };
  const headerEnd = actions ? (
    <span className="compact-card-header-end">{actions}</span>
  ) : null;

  if (!collapsible) {
    return (
      <section className={cn("compact-card", className)}>
        <header className="compact-card-header">
          <span className="compact-card-title">
            <span className="compact-card-title-text">
              <span className="truncate">{title}</span>
              {badge != null ? (
                <span className="w-fit max-w-max shrink-0">{badge}</span>
              ) : null}
            </span>
            <CardHint title={title} hint={hint} />
          </span>
          {headerEnd}
        </header>
        <div className={cn("compact-card-body", bodyClassName)}>{children}</div>
      </section>
    );
  }

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className={cn("compact-card", className)}
    >
      <header className="compact-card-header">
        <span className="compact-card-title">
          <CollapsibleTrigger asChild>
            <button
              type="button"
              className="compact-card-toggle"
              aria-expanded={open}
              aria-label={
                typeof title === "string"
                  ? open
                    ? `Contraer ${title}`
                    : `Expandir ${title}`
                  : undefined
              }
              onClick={(event) => event.stopPropagation()}
            >
              <ChevronDown
                className={cn(
                  "h-3.5 w-3.5 shrink-0 transition-transform",
                  open ? "rotate-180" : "rotate-0",
                )}
                aria-hidden
              />
              <span className="truncate">{title}</span>
              {badge != null ? (
                <span className="w-fit max-w-max shrink-0 justify-self-start">
                  {badge}
                </span>
              ) : null}
            </button>
          </CollapsibleTrigger>
          <CardHint title={title} hint={hint} />
        </span>
        {headerEnd}
      </header>
      {!open && summary ? (
        <p className="compact-card-summary">{summary}</p>
      ) : null}
      <CollapsibleContent>
        <div className={cn("compact-card-body", bodyClassName)}>{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
};
